import { html, nothing, type TemplateResult } from "lit";
import { t } from "../../i18n/index.ts";
import type { AppViewState } from "../app-view-state.ts";
import { loadModelAuthStatusState } from "../controllers/model-auth-status.ts";
import type { GatewayBrowserClient } from "../gateway.ts";

type ProviderId = "anthropic" | "openai" | "google" | "openrouter";

type ProviderDef = {
  id: ProviderId;
  label: string;
  placeholderKey: string;
  consoleUrl: string;
  consoleLabel: string;
};

const PROVIDERS: ProviderDef[] = [
  {
    id: "anthropic",
    label: "Anthropic Claude",
    placeholderKey: "sk-ant-...",
    consoleUrl: "https://console.anthropic.com/settings/keys",
    consoleLabel: "console.anthropic.com",
  },
  {
    id: "openai",
    label: "OpenAI",
    placeholderKey: "sk-...",
    consoleUrl: "https://platform.openai.com/api-keys",
    consoleLabel: "platform.openai.com",
  },
  {
    id: "google",
    label: "Google Gemini",
    placeholderKey: "AIza...",
    consoleUrl: "https://aistudio.google.com/app/apikey",
    consoleLabel: "aistudio.google.com",
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    placeholderKey: "sk-or-...",
    consoleUrl: "https://openrouter.ai/keys",
    consoleLabel: "openrouter.ai",
  },
];

const SETUP_TOKEN_COMMAND = "claude setup-token";

type ProviderStatus = {
  state: "connected" | "expiring" | "missing";
  detail?: string;
};

function statusForProvider(
  modelAuthStatus: AppViewState["modelAuthStatusResult"],
  providerId: string,
): ProviderStatus {
  const providers = modelAuthStatus?.providers ?? [];
  const match = providers.find(
    (entry) => (entry as { provider?: string }).provider === providerId,
  ) as { profiles?: Array<{ status?: string }> } | undefined;
  if (!match || !match.profiles || match.profiles.length === 0) {
    return { state: "missing" };
  }
  const statuses = match.profiles.map((p) => (p.status ?? "ok").toLowerCase());
  if (statuses.includes("expired")) return { state: "missing", detail: "expired" };
  if (statuses.includes("expiring")) return { state: "expiring" };
  return { state: "connected" };
}

function renderStatusBadge(status: ProviderStatus) {
  if (status.state === "connected") {
    return html`<span class="pill ok">${t("connectAi.status.connected")}</span>`;
  }
  if (status.state === "expiring") {
    return html`<span class="pill warn">${t("connectAi.status.expiring")}</span>`;
  }
  return html`<span class="pill muted">${t("connectAi.status.missing")}</span>`;
}

function resetForm(state: AppViewState) {
  state.connectAiOpenProvider = null;
  state.connectAiTokenInput = "";
  state.connectAiShowToken = false;
  state.connectAiError = null;
  state.connectAiAnthropicMethod = "choose";
  state.connectAiCopiedCommand = false;
}

function openForm(state: AppViewState, def: ProviderDef) {
  state.connectAiOpenProvider = def.id;
  state.connectAiTokenInput = "";
  state.connectAiError = null;
  state.connectAiShowToken = false;
  state.connectAiAnthropicMethod = def.id === "anthropic" ? "choose" : "apiKey";
  state.connectAiCopiedCommand = false;
}

async function copyCommand(state: AppViewState) {
  try {
    await navigator.clipboard.writeText(SETUP_TOKEN_COMMAND);
    state.connectAiCopiedCommand = true;
    window.setTimeout(() => {
      state.connectAiCopiedCommand = false;
    }, 1800);
  } catch {
    state.connectAiCopiedCommand = false;
  }
}

function renderAnthropicChooser(state: AppViewState): TemplateResult {
  return html`
    <div class="connect-ai__chooser">
      <p class="muted">${t("connectAi.anthropic.chooseTitle")}</p>
      <div class="connect-ai__chooser-grid">
        <button
          type="button"
          class="connect-ai__chooser-card"
          @click=${() => {
            state.connectAiAnthropicMethod = "setupToken";
            state.connectAiTokenInput = "";
            state.connectAiError = null;
          }}
        >
          <strong>${t("connectAi.anthropic.proLabel")}</strong>
          <span class="muted">${t("connectAi.anthropic.proHint")}</span>
        </button>
        <button
          type="button"
          class="connect-ai__chooser-card"
          @click=${() => {
            state.connectAiAnthropicMethod = "apiKey";
            state.connectAiTokenInput = "";
            state.connectAiError = null;
          }}
        >
          <strong>${t("connectAi.anthropic.apiKeyLabel")}</strong>
          <span class="muted">${t("connectAi.anthropic.apiKeyHint")}</span>
        </button>
      </div>
      <button
        type="button"
        class="btn btn--ghost"
        @click=${() => resetForm(state)}
      >
        ${t("connectAi.modal.cancel")}
      </button>
    </div>
  `;
}

function renderSetupTokenWizard(state: AppViewState, def: ProviderDef): TemplateResult {
  const token = state.connectAiTokenInput;
  const busy = state.connectAiBusy;
  const showToken = state.connectAiShowToken;
  const copied = state.connectAiCopiedCommand;
  const error = state.connectAiError;

  return html`
    <div class="connect-ai__wizard">
      <div class="connect-ai__wizard-head">
        <h4>${t("connectAi.anthropic.wizardTitle")}</h4>
        <button
          type="button"
          class="btn btn--ghost btn--small"
          @click=${() => {
            state.connectAiAnthropicMethod = "choose";
            state.connectAiTokenInput = "";
            state.connectAiError = null;
          }}
        >
          ← ${t("connectAi.anthropic.back")}
        </button>
      </div>

      <ol class="connect-ai__steps">
        <li>
          <span>${t("connectAi.anthropic.step1")}</span>
          <div class="connect-ai__command-row">
            <code class="connect-ai__command">${SETUP_TOKEN_COMMAND}</code>
            <button
              type="button"
              class="btn btn--small"
              @click=${() => void copyCommand(state)}
            >
              ${copied ? t("connectAi.anthropic.copied") : t("connectAi.anthropic.copy")}
            </button>
          </div>
        </li>
        <li>${t("connectAi.anthropic.step2")}</li>
        <li>
          <span>${t("connectAi.anthropic.step3")}</span>
          <div class="login-gate__secret-row">
            <input
              type=${showToken ? "text" : "password"}
              autocomplete="off"
              spellcheck="false"
              placeholder="sk-ant-oat01-..."
              .value=${token}
              ?disabled=${busy}
              @input=${(e: Event) => {
                state.connectAiTokenInput = (e.target as HTMLInputElement).value;
                if (state.connectAiError) {
                  state.connectAiError = null;
                }
              }}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  void submitConnectAi(state, def.id);
                }
              }}
            />
            <button
              type="button"
              class="btn btn--icon"
              @click=${() => {
                state.connectAiShowToken = !state.connectAiShowToken;
              }}
            >
              ${showToken ? "👁" : "👁‍🗨"}
            </button>
          </div>
        </li>
      </ol>

      ${error ? html`<div class="callout danger">${error}</div>` : nothing}

      <div class="connect-ai__actions">
        <button
          type="button"
          class="btn btn--ghost"
          ?disabled=${busy}
          @click=${() => resetForm(state)}
        >
          ${t("connectAi.modal.cancel")}
        </button>
        <button
          type="button"
          class="btn primary"
          ?disabled=${busy || !token.trim()}
          @click=${() => void submitConnectAi(state, def.id)}
        >
          ${busy
            ? t("login.account.submitting")
            : t("connectAi.anthropic.connect")}
        </button>
      </div>
    </div>
  `;
}

function renderApiKeyForm(state: AppViewState, def: ProviderDef): TemplateResult {
  const token = state.connectAiTokenInput;
  const busy = state.connectAiBusy;
  const showToken = state.connectAiShowToken;
  const error = state.connectAiError;

  return html`
    <div class="connect-ai__form">
      ${def.id === "anthropic"
        ? html`
            <div class="connect-ai__wizard-head">
              <h4>${t("connectAi.anthropic.apiKeyTitle")}</h4>
              <button
                type="button"
                class="btn btn--ghost btn--small"
                @click=${() => {
                  state.connectAiAnthropicMethod = "choose";
                  state.connectAiTokenInput = "";
                  state.connectAiError = null;
                }}
              >
                ← ${t("connectAi.anthropic.back")}
              </button>
            </div>
            <p class="muted">${t("connectAi.anthropic.apiKeyBody")}</p>
          `
        : nothing}
      <label class="field">
        <span>${t("connectAi.modal.title", { provider: def.label })}</span>
        <div class="login-gate__secret-row">
          <input
            type=${showToken ? "text" : "password"}
            autocomplete="off"
            spellcheck="false"
            placeholder=${def.placeholderKey}
            .value=${token}
            ?disabled=${busy}
            @input=${(e: Event) => {
              state.connectAiTokenInput = (e.target as HTMLInputElement).value;
              if (state.connectAiError) {
                state.connectAiError = null;
              }
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                void submitConnectAi(state, def.id);
              }
            }}
          />
          <button
            type="button"
            class="btn btn--icon"
            @click=${() => {
              state.connectAiShowToken = !state.connectAiShowToken;
            }}
          >
            ${showToken ? "👁" : "👁‍🗨"}
          </button>
        </div>
      </label>
      ${error ? html`<div class="callout danger">${error}</div>` : nothing}
      <div class="connect-ai__actions">
        <button
          type="button"
          class="btn btn--ghost"
          ?disabled=${busy}
          @click=${() => resetForm(state)}
        >
          ${t("connectAi.modal.cancel")}
        </button>
        <button
          type="button"
          class="btn primary"
          ?disabled=${busy || !token.trim()}
          @click=${() => void submitConnectAi(state, def.id)}
        >
          ${busy ? t("login.account.submitting") : t("connectAi.modal.save")}
        </button>
      </div>
    </div>
  `;
}

function renderProviderCard(state: AppViewState, def: ProviderDef) {
  const status = statusForProvider(state.modelAuthStatusResult, def.id);
  const isOpen = state.connectAiOpenProvider === def.id;

  let body: TemplateResult | typeof nothing = nothing;
  if (!isOpen) {
    body = html`
      <button
        type="button"
        class="btn primary"
        @click=${() => openForm(state, def)}
      >
        ${status.state === "connected"
          ? t("connectAi.replaceKey")
          : t("connectAi.pasteKey")}
      </button>
    `;
  } else if (def.id === "anthropic") {
    if (state.connectAiAnthropicMethod === "choose") {
      body = renderAnthropicChooser(state);
    } else if (state.connectAiAnthropicMethod === "setupToken") {
      body = renderSetupTokenWizard(state, def);
    } else {
      body = renderApiKeyForm(state, def);
    }
  } else {
    body = renderApiKeyForm(state, def);
  }

  return html`
    <article class="connect-ai__card">
      <header class="connect-ai__card-head">
        <div>
          <h3>${def.label}</h3>
          <a class="connect-ai__console-link" href="${def.consoleUrl}" target="_blank" rel="noopener">
            ${def.consoleLabel} →
          </a>
        </div>
        ${renderStatusBadge(status)}
      </header>
      ${body}
    </article>
  `;
}

async function submitConnectAi(state: AppViewState, provider: ProviderDef["id"]) {
  const token = state.connectAiTokenInput.trim();
  if (!token) {
    state.connectAiError = t("connectAi.modal.invalidFormat");
    return;
  }
  if (!state.client) {
    state.connectAiError = t("chat.disconnected");
    return;
  }
  state.connectAiBusy = true;
  state.connectAiError = null;
  try {
    const client = state.client as GatewayBrowserClient;
    await client.request("models.authPaste", { provider, token });
    state.connectAiSuccess = provider;
    resetForm(state);
    try {
      await loadModelAuthStatusState(state, { refresh: true });
    } catch {
      // status read failure is non-fatal for the paste flow
    }
  } catch (err) {
    state.connectAiError =
      err instanceof Error ? err.message : t("connectAi.modal.invalidFormat");
  } finally {
    state.connectAiBusy = false;
  }
}

export function renderConnectAi(state: AppViewState) {
  return html`
    <section class="connect-ai">
      <header class="connect-ai__header">
        <h2>${t("connectAi.title")}</h2>
        <p class="muted">${t("connectAi.subtitle")}</p>
      </header>
      ${state.connectAiSuccess
        ? html`
            <div class="callout ok">
              ${t("connectAi.oauth.success", { provider: state.connectAiSuccess })}
            </div>
          `
        : nothing}
      <div class="connect-ai__grid">
        ${PROVIDERS.map((def) => renderProviderCard(state, def))}
      </div>
    </section>
  `;
}
